import React, { useEffect, useState } from "react";

/** The release this bundle was built from (set by scripts/build-release.sh); "dev" locally. */
export const APP_VERSION = process.env.REACT_APP_VERSION || "dev";

/** Checks for a new release this often while the tab is open (and whenever it regains focus). */
export const UPDATE_CHECK_INTERVAL_MS = 5 * 60 * 1000;

/** The version that is live on the site now, from /version.json; null if it can't be read. */
export async function fetchLiveVersion(): Promise<string | null> {
  try {
    const response = await fetch(`/version.json?t=${Date.now()}`, { cache: "no-store" });
    // a missing file comes back as the app's index.html (SPA fallback), so check it's JSON
    if (!response.ok || !(response.headers.get("content-type") ?? "").includes("json")) {
      return null;
    }
    const data = await response.json();
    return typeof data?.version === "string" ? data.version : null;
  } catch {
    return null;
  }
}

interface UpdateAvailableBannerProps {
  currentVersion?: string;
  fetchVersion?: () => Promise<string | null>;
  intervalMs?: number;
}

/**
 * Tabs that stay open (e.g. Meeting Control all day) never reload the app, so they'd keep
 * running an old release. This notices a newer release and offers a reload; it never forces
 * one, so nobody loses what they're doing.
 */
function UpdateAvailableBanner({ currentVersion = APP_VERSION, fetchVersion = fetchLiveVersion, intervalMs = UPDATE_CHECK_INTERVAL_MS }: UpdateAvailableBannerProps) {
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    if (currentVersion === "dev") return;
    let cancelled = false;
    async function check() {
      const live = await fetchVersion();
      if (!cancelled && live && live !== currentVersion) {
        setAvailable(true);
      }
    }
    const id = setInterval(check, intervalMs);
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [currentVersion, fetchVersion, intervalMs]);

  if (!available) return null;
  return (
    <div className="sl-update-banner" role="status">
      <span>A new version of Synod Link is available.</span>
      <button type="button" className="btn btn-sm btn-light" onClick={() => window.location.reload()}>
        Reload
      </button>
    </div>
  );
}

export default UpdateAvailableBanner;
